import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, TriangleAlert, X as XIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner, ErrorDisplay, EmptyState } from "@/components/loading";
import { useParticipants, useSensorSources, useUserDevices } from "@/api/hooks";
import { buildDeviceLabelMap, buildDeviceLabelMapFromRecords } from "@/lib/device-labels";
import { isEdgeParticipant, isEdgeParticipantId } from "@/lib/upload-alerts";
import { isoDaysAgo } from "@/components/sensor-table/utils";
import {
  buildCharts,
  fetchPlotRecordsWithMeta,
  MAX_RECORDS,
  RECORD_LIMIT_OPTIONS,
  type PlotFilters,
} from "@/components/sensor-charts/build-charts";
import { ChartCard } from "@/components/sensor-charts/visualization-dialog";

const FEATURED_SOURCES: { value: string; label: string; summary: string }[] = [
  { value: "", label: "All Sources", summary: "Records over time and distribution by source" },
  { value: "AMBIENT_AUDIO", label: "Ambient Audio", summary: "Acoustic labels and average confidence scores" },
  { value: "APP_USAGE", label: "App Usage", summary: "Top apps by foreground usage time" },
  { value: "AT_LOCATION", label: "Location", summary: "Location heatmap and GPS trajectory on interactive map" },
  { value: "BLUETOOTH_SCAN", label: "Bluetooth", summary: "Nearby Bluetooth device count over time" },
  { value: "CALL_LOG", label: "Call Log", summary: "Call duration (seconds) over time" },
  { value: "SLEEP", label: "Sleep", summary: "Daily sleep duration (hours)" },
  { value: "survey", label: "Survey", summary: "Survey submission time-of-day and hourly distribution" },
];

const KNOWN_SOURCES = [
  "AMBIENT_AUDIO",
  "APP_USAGE",
  "AT_LOCATION",
  "BLUETOOTH_SCAN",
  "CALL_LOG",
  "SLEEP",
  "HEALTH_CONNECT",
  "survey",
  "PHYSICAL_ACTIVITY",
  "STEP_COUNT",
  "SCREEN_EVENT",
  "NOTIFICATION_POSTED",
  "NOTIFICATION_REMOVED",
  "SMS_LOG",
  "LIGHT",
  "WIFI_EVENT",
  "BATTERY_LEVEL",
  "CHARGING_EVENT",
  "CALENDAR_EVENT",
  "ALIVE",
];

export default function VisualizationPage() {
  const [sourceFilter, setSourceFilter] = useState("");
  const [participantFilter, setParticipantFilter] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("");
  const [fromDate, setFromDate] = useState(() => isoDaysAgo(7));
  const [toDate, setToDate] = useState("");
  const [realParticipantsOnly, setRealParticipantsOnly] = useState(true);
  const [maxRecords, setMaxRecords] = useState<number>(MAX_RECORDS);

  const sources = useSensorSources();
  const participants = useParticipants();
  const userDevices = useUserDevices(participantFilter);

  const allSources = useMemo(() => {
    const set = new Set<string>(KNOWN_SOURCES);
    for (const s of sources.data?.data ?? []) {
      if (s) set.add(s);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [sources.data]);

  const allParticipants = useMemo(
    () => participants.data?.data ?? [],
    [participants.data],
  );

  const realParticipants = useMemo(
    () => allParticipants.filter(isEdgeParticipant),
    [allParticipants],
  );

  const visibleParticipants = realParticipantsOnly
    ? realParticipants
    : allParticipants;

  const realUserIds = useMemo(
    () => realParticipants.map((p) => p.user_id).sort(),
    [realParticipants],
  );

  const realUserIdSet = useMemo(() => new Set(realUserIds), [realUserIds]);

  const participantLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of allParticipants) {
      map[p.user_id] = p.username || p.user_id.slice(0, 8);
    }
    return map;
  }, [allParticipants]);

  const effectiveUserIdFilter = useMemo(() => {
    if (participantFilter) return participantFilter;
    if (
      realParticipantsOnly &&
      realUserIds.length > 0 &&
      realUserIds.length <= 100
    ) {
      return realUserIds.join(",");
    }
    return undefined;
  }, [participantFilter, realParticipantsOnly, realUserIds]);

  const earliestKnownDate = useMemo(() => {
    const pool = participantFilter
      ? allParticipants.filter((p) => p.user_id === participantFilter)
      : visibleParticipants;
    let earliest = "";
    for (const p of pool) {
      const d = p.first_seen?.slice(0, 10) ?? "";
      if (/^\d{4}-\d{2}-\d{2}$/.test(d) && (!earliest || d < earliest)) {
        earliest = d;
      }
    }
    return earliest || isoDaysAgo(30);
  }, [participantFilter, allParticipants, visibleParticipants]);

  const normalizedWindow = useMemo(() => {
    const rawFrom = fromDate || earliestKnownDate;
    const rawTo = toDate || new Date().toISOString().slice(0, 10);
    if (rawFrom && rawTo && rawFrom > rawTo) {
      return { from: rawTo, to: rawFrom };
    }
    return {
      from: rawFrom || undefined,
      to: toDate ? rawTo : undefined,
    };
  }, [fromDate, toDate, earliestKnownDate]);

  const queryFilters = useMemo<PlotFilters>(
    () => ({
      source: sourceFilter || undefined,
      user_id: effectiveUserIdFilter,
      device_id: deviceFilter || undefined,
      from: normalizedWindow.from,
      to: normalizedWindow.to,
      maxRecords,
    }),
    [
      sourceFilter,
      effectiveUserIdFilter,
      deviceFilter,
      normalizedWindow.from,
      normalizedWindow.to,
      maxRecords,
    ],
  );

  const waitingForParticipants =
    realParticipantsOnly && !participantFilter && participants.isLoading;

  const query = useQuery({
    queryKey: ["plot", queryFilters],
    queryFn: () => fetchPlotRecordsWithMeta(queryFilters),
    enabled: !waitingForParticipants,
  });

  const rawRecords = useMemo(() => query.data?.records ?? [], [query.data]);

  const records = useMemo(() => {
    if (!realParticipantsOnly) return rawRecords;
    return rawRecords.filter(
      (r) =>
        realUserIdSet.has(r.participant_id) ||
        isEdgeParticipantId(r.participant_id) ||
        isEdgeParticipantId(participantLabels[r.participant_id] ?? ""),
    );
  }, [rawRecords, realParticipantsOnly, realUserIdSet, participantLabels]);

  const charts = useMemo(
    () =>
      buildCharts(records, sourceFilter, {
        from: normalizedWindow.from,
        to: normalizedWindow.to,
      }),
    [records, sourceFilter, normalizedWindow.from, normalizedWindow.to],
  );

  const userDeviceLabels = useMemo(
    () => buildDeviceLabelMap(userDevices.data?.data ?? []),
    [userDevices.data],
  );

  const recordDeviceLabels = useMemo(
    () => buildDeviceLabelMapFromRecords(records),
    [records],
  );

  const deviceLabels = useMemo(
    () => ({ ...recordDeviceLabels, ...userDeviceLabels }),
    [recordDeviceLabels, userDeviceLabels],
  );

  const deviceOptions = useMemo(() => {
    const byLabel = new Map<string, string[]>();
    const addId = (id: string) => {
      const label = deviceLabels[id] ?? id.slice(0, 10) + "…";
      const ids = byLabel.get(label) ?? [];
      if (!ids.includes(id)) ids.push(id);
      byLabel.set(label, ids);
    };
    for (const id of Object.keys(userDeviceLabels)) addId(id);
    for (const r of records) {
      if (r.device_id) addId(r.device_id);
    }
    return Array.from(byLabel.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, ids]) => ({ label, value: ids.join(",") }));
  }, [records, deviceLabels, userDeviceLabels]);

  const renderParticipantValue = (val: unknown): ReactNode => {
    if (!val) return null;
    const s = String(val);
    return participantLabels[s] ?? s.slice(0, 8);
  };

  const renderDeviceValue = (val: unknown): ReactNode => {
    if (!val) return null;
    const firstId = String(val).split(",")[0];
    return deviceLabels[firstId] ?? firstId.slice(0, 10) + "…";
  };

  const handleRealParticipantsToggle = (nextRealOnly: boolean) => {
    setRealParticipantsOnly(nextRealOnly);
    if (
      nextRealOnly &&
      participantFilter &&
      !realUserIdSet.has(participantFilter) &&
      !isEdgeParticipantId(participantFilter)
    ) {
      setParticipantFilter("");
      setDeviceFilter("");
    }
  };

  const onTimeChip = (kind: "24h" | "3d" | "7d" | "30d" | "all") => {
    if (kind === "24h") setFromDate(isoDaysAgo(1));
    else if (kind === "3d") setFromDate(isoDaysAgo(3));
    else if (kind === "7d") setFromDate(isoDaysAgo(7));
    else if (kind === "30d") setFromDate(isoDaysAgo(30));
    else setFromDate("");
    setToDate("");
  };

  const hasActiveFilters =
    Boolean(sourceFilter) ||
    Boolean(participantFilter) ||
    Boolean(deviceFilter) ||
    fromDate !== isoDaysAgo(7) ||
    Boolean(toDate) ||
    !realParticipantsOnly ||
    maxRecords !== MAX_RECORDS;

  const onResetFilters = () => {
    setSourceFilter("");
    setParticipantFilter("");
    setDeviceFilter("");
    setFromDate(isoDaysAgo(7));
    setToDate("");
    setRealParticipantsOnly(true);
    setMaxRecords(MAX_RECORDS);
  };

  const activeFeatured = FEATURED_SOURCES.find(
    (s) => s.value.toUpperCase() === sourceFilter.toUpperCase(),
  );

  const windowDescription = useMemo(() => {
    if (fromDate && toDate) return `${fromDate} → ${toDate}`;
    if (fromDate) return `since ${fromDate}`;
    if (toDate) return `${earliestKnownDate} → ${toDate}`;
    return `all time (since ${earliestKnownDate})`;
  }, [fromDate, toDate, earliestKnownDate]);

  const isLoading = waitingForParticipants || query.isLoading;
  const capped = Boolean(query.data?.truncated);
  const totalSlices = query.data?.totalSlices ?? 1;
  const completedSlices = query.data?.completedSlices ?? 1;
  const nextLimitOption = RECORD_LIMIT_OPTIONS.find((opt) => opt > maxRecords);

  return (
    <div className="p-4 space-y-5 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-[#1E293B]">Visualization</h1>
          <p className="text-sm text-muted-foreground">
            {activeFeatured?.summary ?? "Interactive charts across sensor and survey data"}
          </p>
        </div>

        <div
          role="group"
          aria-label="Participant filter scope"
          className="inline-flex items-center self-start rounded-lg border border-border bg-muted/40 p-0.5 sm:self-auto"
        >
          <Button
            type="button"
            size="sm"
            variant={realParticipantsOnly ? "default" : "ghost"}
            className="h-7 px-3 text-xs"
            onClick={() => handleRealParticipantsToggle(true)}
            title="Show only real participants with IDs edgeXXX or EDGEXXX"
          >
            Real (edgeXXX)
          </Button>
          <Button
            type="button"
            size="sm"
            variant={!realParticipantsOnly ? "default" : "ghost"}
            className="h-7 px-3 text-xs"
            onClick={() => handleRealParticipantsToggle(false)}
            title="Show all participants"
          >
            All
          </Button>
        </div>
      </div>

      {/* Quick data type selector pills */}
      <div className="flex flex-wrap gap-1.5">
        {FEATURED_SOURCES.map((item) => {
          const active = item.value.toUpperCase() === sourceFilter.toUpperCase();
          return (
            <Button
              key={item.value || "__all__"}
              type="button"
              size="sm"
              variant={active ? "default" : "outline"}
              onClick={() => setSourceFilter(item.value)}
            >
              {item.label}
            </Button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Source</label>
          <Select
            value={sourceFilter}
            onValueChange={(val) => setSourceFilter(val === "__all__" ? "" : val ?? "")}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sources</SelectItem>
              {allSources.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Participant</label>
          <Select
            value={participantFilter}
            onValueChange={(val) => {
              setParticipantFilter(val === "__all__" ? "" : val ?? "");
              setDeviceFilter("");
            }}
          >
            <SelectTrigger className="w-48">
              <SelectValue
                placeholder={
                  realParticipantsOnly ? "Real participants" : "All participants"
                }
              >
                {renderParticipantValue}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">
                {realParticipantsOnly ? "Real participants" : "All participants"}
              </SelectItem>
              {visibleParticipants.map((p) => (
                <SelectItem key={p.user_id} value={p.user_id}>
                  {p.username || p.user_id.slice(0, 8)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Device</label>
          <Select
            value={deviceFilter}
            onValueChange={(val) => setDeviceFilter(val === "__all__" ? "" : val ?? "")}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All devices">
                {renderDeviceValue}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All devices</SelectItem>
              {deviceOptions.map((d) => (
                <SelectItem key={d.value} value={d.value}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">From</label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-36"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">To</label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-36"
          />
        </div>

        <div className="flex flex-wrap gap-1">
          <Button variant="outline" size="sm" onClick={() => onTimeChip("24h")}>
            24h
          </Button>
          <Button variant="outline" size="sm" onClick={() => onTimeChip("3d")}>
            3d
          </Button>
          <Button variant="outline" size="sm" onClick={() => onTimeChip("7d")}>
            7d
          </Button>
          <Button variant="outline" size="sm" onClick={() => onTimeChip("30d")}>
            30d
          </Button>
          <Button variant="outline" size="sm" onClick={() => onTimeChip("all")}>
            All
          </Button>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            Max records
          </label>
          <Select
            value={String(maxRecords)}
            onValueChange={(val) => {
              const parsed = Number(val);
              if (Number.isFinite(parsed) && parsed > 0) {
                setMaxRecords(parsed);
              }
            }}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RECORD_LIMIT_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={String(opt)}>
                  {opt.toLocaleString()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onResetFilters}
            className="text-muted-foreground"
          >
            <XIcon className="size-3.5" />
            Reset
          </Button>
        )}
      </div>

      {/* Status bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-b py-2">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline">{sourceFilter || "All sources"}</Badge>
          <span>·</span>
          <Badge variant="outline">
            {realParticipantsOnly ? "Real (edgeXXX)" : "All participants"}
          </Badge>
          <span>·</span>
          <span>{windowDescription}</span>
          {!isLoading && !query.isError && (
            <>
              <span>·</span>
              <span>
                <span className="font-medium text-foreground">
                  {records.length.toLocaleString()}
                </span>{" "}
                records
                {capped
                  ? totalSlices > 1
                    ? ` (sampled across ${totalSlices} date segments)`
                    : ` (hit ${maxRecords.toLocaleString()} cap)`
                  : totalSlices > 1
                    ? ` (full range across ${totalSlices} days)`
                    : ""}
              </span>
            </>
          )}
          {query.isFetching && !isLoading && (
            <span className="text-primary">refreshing…</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            title="Refresh"
          >
            <RefreshCw className="size-3.5" />
          </Button>
        </div>
      </div>

      {!isLoading && !query.isError && capped && (
        <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50/90 p-3 text-xs text-amber-950 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 font-semibold text-amber-900">
              <TriangleAlert className="size-4 shrink-0 text-amber-700" />
              <span>
                Date range exceeds {maxRecords.toLocaleString()} records — sampled
                evenly across {totalSlices} date segment{totalSlices === 1 ? "" : "s"}
              </span>
            </div>
            <div className="text-amber-900/90">
              Loaded{" "}
              <span className="font-mono font-medium">
                {records.length.toLocaleString()}
              </span>{" "}
              records distributed across the full selected range ({completedSlices} of{" "}
              {totalSlices} date segments loaded in full) so earlier days are never
              starved by newer days.
            </div>
          </div>
          {nextLimitOption && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 border-amber-400 bg-white text-amber-950 hover:bg-amber-100"
              onClick={() => setMaxRecords(nextLimitOption)}
            >
              Load up to {nextLimitOption.toLocaleString()} records
            </Button>
          )}
        </div>
      )}

      {/* Charts */}
      {isLoading ? (
        <Spinner />
      ) : query.isError ? (
        <ErrorDisplay message={query.error.message} onRetry={() => query.refetch()} />
      ) : charts.length === 0 ? (
        <EmptyState message="No data to plot for these filters" />
      ) : (
        <div className={`grid gap-4 ${charts.length > 1 ? "lg:grid-cols-2" : "grid-cols-1"}`}>
          {charts.map((spec) => (
            <ChartCard key={spec.id} spec={spec} />
          ))}
        </div>
      )}
    </div>
  );
}
